import React, { memo, useState, useEffect, useRef } from 'react';
import formsService from '../../utils/formsService';

// Componente para manejar campos de imagen con miniaturas
const ImageField = memo(({ question, value, onChange, handleFileChange, readonly }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Marcar componente como montado
    isMountedRef.current = true;

    const loadThumbnail = async () => {
      // Cancelar request anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Si value es un objeto de archivo (nuevo archivo seleccionado)
      if (value && typeof value === 'object' && value.isFile && value.file) {
        if (!isMountedRef.current) return;
        setLoading(true);
        setError(null);
        try {
          // Crear URL del blob para mostrar la imagen seleccionada
          const imageUrl = URL.createObjectURL(value.file);
          if (isMountedRef.current) {
            setThumbnailUrl(imageUrl);
          }
        } catch (err) {
          // // console.error('Error creating blob URL:', err);
          if (isMountedRef.current) {
            setError('No se pudo mostrar la imagen seleccionada');
          }
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      }
      // Si value es un string (imagen existente)
      else if (value && typeof value === 'string' && value.trim()) {
        if (!isMountedRef.current) return;
        setLoading(true);
        setError(null);
        
        // Crear nuevo AbortController para este request
        abortControllerRef.current = new AbortController();
        
        try {
          const imageUrl = await formsService.getImageThumbnail(value, {
            signal: abortControllerRef.current.signal
          });
          if (isMountedRef.current) {
            setThumbnailUrl(imageUrl);
          }
        } catch (err) {
          // // console.error('Error loading thumbnail:', err);
          if (isMountedRef.current && err.name !== 'AbortError') {
            setError('No se pudo cargar la miniatura');
          }
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      } else {
        if (isMountedRef.current) {
          setThumbnailUrl(null);
          setError(null);
        }
      }
    };

    loadThumbnail();

    // Cleanup function
    return () => {
      // Marcar componente como desmontado
      isMountedRef.current = false;
      
      // Cancelar requests pendientes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Liberar URLs de blob
      if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [value]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="file-field">
      <input
        type="file"
        id={question.name}
        accept="image/*"
        onChange={handleFileChange}
        required={question.required}
        disabled={readonly}
      />
      
      {value && (
        <div style={{ marginTop: '12px' }}>
          
          {loading && (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666',
              fontSize: '14px'
            }}>
              Cargando miniatura...
            </div>
          )}
          
          {error && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
          
          {thumbnailUrl && !loading && !error && (
            <div style={{
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              padding: '8px',
              backgroundColor: '#f9fafb',
              display: 'inline-block'
            }}>
              <img 
                src={thumbnailUrl}
                alt={`Miniatura de ${value && typeof value === 'object' ? value.name : value}`}
                style={{
                  maxWidth: '150px',
                  maxHeight: '100px',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
                onError={() => {
                  setError('Error al mostrar la imagen');
                  setThumbnailUrl(null);
                }}
              />
              {/* Mostrar información del archivo si es un archivo nuevo */}
              {value && typeof value === 'object' && value.isFile && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center'
                }}>
                  <div><strong>{value.name}</strong></div>
                  <div>{(value.size / 1024).toFixed(1)} KB</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ImageField.displayName = 'ImageField';

export default ImageField;